import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../core/AppContext';

// Self-contained GPU worker power control (start/stop to save cost). Keeps its
// own state + fetches so it doesn't touch the shared AppContext. Reads the auth
// token from localStorage (same place the app stores it). Gated by the backend
// on manage_config; rendered only for can('manage_config').
function GpuWorkerPanel() {
  const [state, setState] = useState(null);     // ec2 state name, or 'NOT_FOUND'
  const [instanceId, setInstanceId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/gpu-worker', { headers: headers() });
      const d = await res.json();
      if (d.status === 'OK') { setState(d.state); setInstanceId(d.instance_id); setMsg(''); }
      else if (d.status === 'NOT_FOUND') { setState('NOT_FOUND'); setMsg(d.message || ''); }
      else { setMsg(d.error || 'Error reading GPU state'); }
    } catch (e) { setMsg('Error contacting server'); }
  }, []);

  // Poll while transitioning (pending/stopping) so the button state tracks reality.
  useEffect(() => {
    refresh();
    const transitioning = state === 'pending' || state === 'stopping';
    const t = setInterval(refresh, transitioning ? 5000 : 20000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [refresh, state === 'pending' || state === 'stopping']);

  const act = async (action) => {
    setBusy(true); setMsg(action === 'start' ? 'Starting…' : 'Stopping…');
    try {
      const res = await fetch('/api/gpu-worker', { method: 'POST', headers: headers(), body: JSON.stringify({ action }) });
      const d = await res.json();
      if (d.status === 'OK') { setState(d.state); setMsg(d.note || `${action} requested`); }
      else { setMsg(d.error || d.message || 'Action failed'); }
    } catch (e) { setMsg('Error contacting server'); }
    finally { setBusy(false); setTimeout(refresh, 2000); }
  };

  const color = state === 'running' ? '#28a745' : state === 'stopped' ? '#718096'
    : (state === 'pending' || state === 'stopping') ? '#b7791f' : '#b00020';
  const label = state === 'NOT_FOUND' ? 'not found' : (state || 'checking…');

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
      <h3 style={{ marginTop: 0 }}>🖥️ GPU Render Worker</h3>
      <p style={{ fontSize: '12px', color: '#666' }}>
        Start the GPU worker only when you need voice-clone / lip-sync renders, and stop it when idle to save cost
        (the g-series GPU bills per hour while running; EBS storage persists when stopped).
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px' }}>Status:
          <strong style={{ color, marginLeft: '6px', textTransform: 'uppercase' }}>{label}</strong>
        </span>
        {instanceId && <span style={{ fontSize: '11px', color: '#a0aec0', fontFamily: 'monospace' }}>{instanceId}</span>}
        <button onClick={() => act('start')} disabled={busy || state === 'running' || state === 'pending' || state === 'NOT_FOUND'}
          style={{ padding: '7px 16px', border: 'none', borderRadius: '4px', fontWeight: 'bold',
            background: (busy || state === 'running' || state === 'pending') ? '#9ae6b4' : '#28a745', color: '#fff',
            cursor: (busy || state === 'running' || state === 'pending') ? 'default' : 'pointer' }}>
          ▶ Start
        </button>
        <button onClick={() => act('stop')} disabled={busy || state === 'stopped' || state === 'stopping' || state === 'NOT_FOUND'}
          style={{ padding: '7px 16px', border: 'none', borderRadius: '4px', fontWeight: 'bold',
            background: (busy || state === 'stopped' || state === 'stopping') ? '#cbd5e0' : '#dc3545', color: '#fff',
            cursor: (busy || state === 'stopped' || state === 'stopping') ? 'default' : 'pointer' }}>
          ■ Stop
        </button>
        <button onClick={refresh} disabled={busy}
          style={{ padding: '7px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>
          ↻ Refresh
        </button>
      </div>
      {(state === 'pending' || state === 'stopping') && (
        <p style={{ fontSize: '11px', color: '#b7791f', marginTop: '8px', marginBottom: 0 }}>
          Transitioning… a fresh start also re-runs first-boot setup (model warm-up) before renders are ready.
        </p>
      )}
      {msg && <p style={{ fontSize: '12px', color: '#555', marginTop: '8px', marginBottom: 0 }}>{msg}</p>}
    </div>
  );
}

// Council-OSS GPU judges power/status panel. Two boxes (primary gpt-oss-20b,
// chief granite-3.3-2b), each a full A10G. Mirrors GpuWorkerPanel but with a
// live SERVING probe (not just EC2 state) and per-box + both controls. The
// "Test connection" button hits /v1/models so you know the model actually
// answers, not merely that EC2 is on. Gated on manage_config.
function CouncilOssGpuPanel() {
  const [boxes, setBoxes] = useState({ primary: null, chief: null });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/council-oss/status', { headers: headers() });
      const d = await res.json();
      if (d.status === 'OK') { setBoxes({ primary: d.primary, chief: d.chief }); setMsg(''); }
      else { setMsg(d.error || 'Error reading status'); }
    } catch (e) { setMsg('Error contacting server'); }
  }, []);

  // Poll faster while any box is transitioning (pending/stopping) or still booting vLLM.
  const transitioning = ['primary', 'chief'].some((k) => {
    const b = boxes[k]; if (!b) return false;
    return b.state === 'pending' || b.state === 'stopping' || b.serving === 'booting';
  });
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, transitioning ? 7000 : 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [refresh, transitioning]);

  const power = async (action, target) => {
    setBusy(true); setMsg(`${action === 'start' ? 'Starting' : 'Stopping'} ${target}…`);
    try {
      const res = await fetch('/api/council-oss/power', { method: 'POST', headers: headers(), body: JSON.stringify({ action, target }) });
      const d = await res.json();
      setMsg(d.status === 'OK' ? `${action} ${target} requested` : (d.error || 'Action failed'));
    } catch (e) { setMsg('Error contacting server'); }
    finally { setBusy(false); setTimeout(refresh, 2500); }
  };

  const test = async () => {
    setBusy(true); setMsg('Testing connections…');
    try {
      const res = await fetch('/api/council-oss/test', { method: 'POST', headers: headers() });
      const d = await res.json();
      if (d.status === 'OK') {
        const p = d.boxes.primary || {}; const c = d.boxes.chief || {};
        setMsg(`Primary: ${p.serving || 'unknown'} · Chief: ${c.serving || 'unknown'}`);
        setBoxes({ primary: p, chief: c });
      } else { setMsg(d.error || 'Test failed'); }
    } catch (e) { setMsg('Error contacting server'); }
    finally { setBusy(false); }
  };

  const pill = (serving) => {
    const map = {
      serving: { bg: '#28a745', label: 'SERVING' },
      booting: { bg: '#b7791f', label: 'BOOTING' },
      stopped: { bg: '#718096', label: 'STOPPED' },
      unknown: { bg: '#b00020', label: 'UNKNOWN' },
    };
    const s = map[serving] || map.unknown;
    return <strong style={{ color: s.bg, marginLeft: '6px' }}>{s.label}</strong>;
  };

  const row = (key, title, model) => {
    const b = boxes[key] || {};
    const serving = b.serving || 'unknown';
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid #edf2f7' }}>
        <span style={{ minWidth: '190px', fontSize: '13px' }}><strong>{title}</strong><br /><span style={{ fontSize: '11px', color: '#a0aec0', fontFamily: 'monospace' }}>{model}</span></span>
        <span style={{ fontSize: '13px' }}>Status:{pill(serving)}</span>
        {b.private_ip && <span style={{ fontSize: '11px', color: '#a0aec0', fontFamily: 'monospace' }}>{b.private_ip}</span>}
        <button onClick={() => power('start', key)} disabled={busy || serving === 'serving' || b.state === 'running' || b.state === 'pending'}
          style={{ padding: '5px 12px', border: 'none', borderRadius: '4px', fontWeight: 'bold', background: (busy || b.state === 'running' || b.state === 'pending') ? '#9ae6b4' : '#28a745', color: '#fff', cursor: 'pointer', fontSize: '12px' }}>▶ Start</button>
        <button onClick={() => power('stop', key)} disabled={busy || b.state === 'stopped' || b.state === 'stopping'}
          style={{ padding: '5px 12px', border: 'none', borderRadius: '4px', fontWeight: 'bold', background: (busy || b.state === 'stopped' || b.state === 'stopping') ? '#cbd5e0' : '#dc3545', color: '#fff', cursor: 'pointer', fontSize: '12px' }}>■ Stop</button>
      </div>
    );
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
      <h3 style={{ marginTop: 0 }}>🧮 Council OSS GPU Judges</h3>
      <p style={{ fontSize: '12px', color: '#666' }}>
        Two self-hosted vLLM judge boxes, each on its own A10G. Start them before an eval/validation session and stop when idle to save cost.
        <strong> Status reflects whether vLLM is actually serving</strong> (not just whether EC2 is on): SERVING = model answers, BOOTING = instance up but model still loading, STOPPED = instance off.
      </p>
      {row('primary', 'Primary judge', 'gpt-oss-20b :8000')}
      {row('chief', 'Chief judge', 'granite-3.3-2b :8000')}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' }}>
        <button onClick={() => power('start', 'both')} disabled={busy}
          style={{ padding: '7px 16px', border: 'none', borderRadius: '4px', fontWeight: 'bold', background: '#28a745', color: '#fff', cursor: 'pointer' }}>▶ Start both</button>
        <button onClick={() => power('stop', 'both')} disabled={busy}
          style={{ padding: '7px 16px', border: 'none', borderRadius: '4px', fontWeight: 'bold', background: '#dc3545', color: '#fff', cursor: 'pointer' }}>■ Stop both</button>
        <button onClick={test} disabled={busy}
          style={{ padding: '7px 16px', border: '1px solid #3182ce', borderRadius: '4px', fontWeight: 'bold', background: '#fff', color: '#3182ce', cursor: 'pointer' }}>🔌 Test connection</button>
        <button onClick={refresh} disabled={busy}
          style={{ padding: '7px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>↻ Refresh</button>
      </div>
      <p style={{ fontSize: '11px', color: '#718096', marginTop: '10px', marginBottom: 0 }}>
        Auto-schedule: stop 5:00 PM, start 9:00 AM (America/Denver) — installed but currently OFF (enable in Terraform: <code>enable_scheduler=true</code>).
      </p>
      {msg && <p style={{ fontSize: '12px', color: '#555', marginTop: '8px', marginBottom: 0 }}>{msg}</p>}
    </div>
  );
}

// LLM council settings: judge mode (direct Bedrock vs A2A agents on AgentCore),
// the judge list, and thresholds. Saved via the shared /config endpoint (key "council").
const COUNCIL_DEFAULTS = {
  mode: 'direct',
  judges: [
    { name: 'gptoss', model_id: 'openai.gpt-oss-120b-1:0', runtime_arn: '', enabled: true },
    { name: 'nova', model_id: 'amazon.nova-pro-v1:0', runtime_arn: '', enabled: true },
    { name: 'llama', model_id: 'us.meta.llama3-3-70b-instruct-v1:0', runtime_arn: '', enabled: true },
  ],
  disagree_spread: 30, min_win_rate: 50, max_prompts: 8, compare_prompts: 6, both_orders: true, include_safety: true,
  use_in_eval: false,   // Evaluate grading: false = single judge, true = full council
  show_on_scorecard: false,   // show judge / council details on the model scorecard + Evaluate popup
};

// Challenger ranking-agent cadence. Stored in AppConfig['challenger'].schedule via
// the shared /config endpoint (same key the Challenger tab dropdown and the backend
// schedule_due() read). Mirror of the control in the Challenger tab.
const CHALLENGER_SCHEDULE_OPTS = {
  off: 'Off (manual only)', '30m': 'Every 30 minutes', '1h': 'Every hour',
  '6h': 'Every 6 hours', '1d': 'Once a day',
};

function ChallengerSchedulePanel({ adminConfig, saveAdminConfig }) {
  const challenger = (adminConfig && adminConfig.challenger) || {};
  const [sched, setSched] = useState(challenger.schedule || 'off');
  useEffect(() => { setSched(((adminConfig && adminConfig.challenger) || {}).schedule || 'off'); }, [adminConfig]);
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
      <h3 style={{ marginTop: 0 }}>🤖 Challenger ranking agent</h3>
      <p style={{ fontSize: '12px', color: '#666' }}>
        How often the agent re-ranks All Models + EchoStar Approved and refreshes the top 1–3 challengers per VM.
        Ranks over stored scores (no live eval). You can also run it on demand from the Challenger tab.
      </p>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '13px' }}>Run every:</label>
        <select value={sched} onChange={e => setSched(e.target.value)} style={{ padding: '6px', fontSize: '13px', borderRadius: '4px' }}>
          {Object.keys(CHALLENGER_SCHEDULE_OPTS).map(k => <option key={k} value={k}>{CHALLENGER_SCHEDULE_OPTS[k]}</option>)}
        </select>
        <button onClick={() => saveAdminConfig('challenger', { ...challenger, schedule: sched })}
          style={{ padding: '8px 16px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>
          Save schedule
        </button>
      </div>
      <p style={{ fontSize: '11px', color: '#a0aec0', marginBottom: 0, marginTop: '8px' }}>
        A base timer fires every ~10 min; this cadence gates whether each tick actually runs. "Off" disables auto-runs.
      </p>
    </div>
  );
}

function CouncilPanel({ adminConfig, saveAdminConfig }) {
  const saved = (adminConfig && adminConfig.council) || null;
  const [c, setC] = useState(COUNCIL_DEFAULTS);
  const [paste, setPaste] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (saved && typeof saved === 'object') setC({ ...COUNCIL_DEFAULTS, ...saved, judges: (saved.judges && saved.judges.length) ? saved.judges : COUNCIL_DEFAULTS.judges });
  }, [saved]);
  const setJ = (i, k, v) => setC(prev => ({ ...prev, judges: prev.judges.map((j, n) => (n === i ? { ...j, [k]: v } : j)) }));
  const inp = { padding: '5px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' };
  const a2aReady = c.judges.some(j => j.enabled && j.runtime_arn);
  const applyPaste = () => {
    try {
      const parsed = JSON.parse(paste);
      // A council_chief_json object (not a list) configures the chief judge.
      if (parsed && !Array.isArray(parsed) && (parsed.endpoint || parsed.model_id)) {
        setC(prev => ({ ...prev, chief_judge: { model_id: parsed.model_id || '', endpoint: parsed.endpoint || '', api_key_ssm: parsed.api_key_ssm || '' } }));
        setMsg('Loaded chief-judge config. Click Save.');
        return;
      }
      const arr = parsed;
      if (!Array.isArray(arr) || !arr.length) throw new Error('expected a JSON list, or a chief-judge object');
      // Merge by name so loading 45-council-oss output adds the self-hosted judge
      // without wiping the AgentCore judges from 40-council (and vice versa).
      const incoming = arr.map(j => ({ name: j.name, model_id: j.model_id, runtime_arn: j.runtime_arn || '',
        endpoint: j.endpoint || '', api_key_ssm: j.api_key_ssm || '', enabled: j.enabled !== false }));
      setC(prev => {
        const byName = {};
        prev.judges.forEach(j => { byName[j.name] = j; });
        incoming.forEach(j => { byName[j.name] = { ...(byName[j.name] || {}), ...j }; });
        return { ...prev, judges: Object.values(byName) };
      });
      setMsg(`Loaded ${arr.length} judge(s) from Terraform output (merged by name). Click Save.`);
    } catch (e) { setMsg(`Could not read that: ${e.message}`); }
  };
  const num = (k, lo, hi) => (
    <label style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {({ disagree_spread: 'Disagreement flag (score spread)', min_win_rate: 'Min challenger win rate %', max_prompts: 'Prompts per Evaluate', compare_prompts: 'Prompts per Compare' })[k]}
      <input type="number" min={lo} max={hi} value={c[k]} onChange={e => setC({ ...c, [k]: e.target.value })} style={{ ...inp, width: '120px' }} />
    </label>
  );
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
      <h3 style={{ marginTop: 0 }}>🧑⚖️ LLM Council (Evaluate & Compare)</h3>
      <p style={{ fontSize: '12px', color: '#666', marginTop: '-6px' }}>
        Judges grade every answer; a judge never grades a model from its own family. In <b>A2A</b> mode each judge is an agent on
        Bedrock AgentCore (Terraform stack <code>40-council</code>); a failed agent call falls back to a direct Bedrock call.
        A judge with a <b>self-hosted endpoint</b> (stack <code>45-council-oss</code>, vLLM on EC2) is always called directly.
      </p>
      {(() => {
        // Evaluate always runs the golden test suite live on the real model; this only
        // chooses how the answers are graded. Stored as council.use_in_eval (true = full council).
        const firstJudge = (c.judges || []).find(j => j.enabled);
        const opt = (val, title, desc) => (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '6px 8px', borderRadius: '4px', cursor: 'pointer',
                          background: !!c.use_in_eval === val ? '#fff' : 'transparent', border: `1px solid ${!!c.use_in_eval === val ? '#90cdf4' : 'transparent'}` }}>
            <input type="radio" name="eval_grading" checked={!!c.use_in_eval === val} onChange={() => setC({ ...c, use_in_eval: val })} style={{ marginTop: '2px' }} />
            <span>
              <span style={{ fontSize: '13px', fontWeight: 'bold' }}>{title}</span>
              <span style={{ display: 'block', fontSize: '11px', color: '#555' }}>{desc}</span>
            </span>
          </label>
        );
        return (
          <div style={{ background: '#f0f7ff', border: '1px solid #bee3f8', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px' }}>Evaluate grading</div>
            <div style={{ fontSize: '11px', color: '#555', marginBottom: '6px' }}>
              Evaluate always runs the golden test suite live on the real model (Bedrock, Google Vertex or a self-hosted / temporary GPU).
              This setting only chooses who grades the answers. Scores are never simulated.
            </div>
            {opt(false, 'Single judge (default) — cheaper, faster',
              `One judge grades every answer: the first enabled judge in the list below that is not from the evaluated model's own family`
              + (firstJudge ? ` (currently “${firstJudge.name}”, or the next one if it's the same family as the model).` : '. No judge is enabled yet.')
              + ' No chief judge.')}
            {opt(true, 'Full council — more robust',
              'Every enabled judge below grades each answer; the median is the score and big disagreements are flagged. The chief judge decides, if one is set.')}
            <div style={{ fontSize: '11px', color: '#777', marginTop: '6px' }}>
              Compare and the Council Validation tab always use the full council, whatever is chosen here.
            </div>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #bee3f8', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!c.show_on_scorecard} onChange={e => setC({ ...c, show_on_scorecard: e.target.checked })} style={{ marginTop: '2px' }} />
              <span>
                <span style={{ fontSize: '13px', fontWeight: 'bold' }}>Show council details on the model scorecard</span>
                <span style={{ display: 'block', fontSize: '11px', color: '#555' }}>
                  {c.show_on_scorecard
                    ? 'ON — the scorecard says which judge(s) graded each run and flags judge disagreements, and the Evaluate popup shows the live judge-by-judge trace.'
                    : 'OFF (default) — the scorecard shows the scores only ("judge-graded"); no judge names, votes or live council trace. Grading is unchanged.'}
                </span>
              </span>
            </label>
          </div>
        );
      })()}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '10px', fontSize: '12px' }}>
        <b>Judge mode</b>
        <label><input type="radio" checked={c.mode === 'direct'} onChange={() => setC({ ...c, mode: 'direct' })} /> Direct (Bedrock calls from the backend)</label>
        <label style={{ opacity: a2aReady ? 1 : 0.6 }} title={a2aReady ? '' : 'Add a runtime ARN to at least one enabled judge'}>
          <input type="radio" checked={c.mode === 'a2a'} disabled={!a2aReady} onChange={() => setC({ ...c, mode: 'a2a' })} /> A2A agents on AgentCore
        </label>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '8px' }}>
        <thead><tr style={{ textAlign: 'left', background: '#f7fafc' }}>
          <th style={{ padding: '6px' }}>On</th><th style={{ padding: '6px' }}>Name</th><th style={{ padding: '6px' }}>Bedrock model</th>
          <th style={{ padding: '6px' }}>AgentCore runtime ARN (A2A)</th>
          <th style={{ padding: '6px' }}>Self-hosted endpoint / key SSM</th><th></th></tr></thead>
        <tbody>
          {c.judges.map((j, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #edf2f7' }}>
              <td style={{ padding: '4px 6px' }}><input type="checkbox" checked={!!j.enabled} onChange={e => setJ(i, 'enabled', e.target.checked)} /></td>
              <td style={{ padding: '4px 6px' }}><input value={j.name} onChange={e => setJ(i, 'name', e.target.value)} style={{ ...inp, width: '90px' }} /></td>
              <td style={{ padding: '4px 6px' }}><input value={j.model_id} onChange={e => setJ(i, 'model_id', e.target.value)} style={{ ...inp, width: '100%' }} /></td>
              <td style={{ padding: '4px 6px' }}><input value={j.runtime_arn || ''} disabled={!!j.endpoint} placeholder={j.endpoint ? 'n/a (self-hosted)' : 'arn:aws:bedrock-agentcore:…:runtime/eea_council_…'} onChange={e => setJ(i, 'runtime_arn', e.target.value)} style={{ ...inp, width: '100%' }} /></td>
              <td style={{ padding: '4px 6px' }}>
                <input value={j.endpoint || ''} placeholder="http://10.0.x.x:8000/v1" onChange={e => setJ(i, 'endpoint', e.target.value)} style={{ ...inp, width: '100%', marginBottom: '2px' }} />
                <input value={j.api_key_ssm || ''} placeholder="/eea/council/oss-judge-api-key" onChange={e => setJ(i, 'api_key_ssm', e.target.value)} style={{ ...inp, width: '100%' }} />
              </td>
              <td style={{ padding: '4px 6px' }}><button onClick={() => setC({ ...c, judges: c.judges.filter((_, n) => n !== i) })} style={{ border: 'none', background: 'transparent', color: '#e53e3e', cursor: 'pointer' }} title="Remove judge">🗑️</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={() => setC({ ...c, judges: [...c.judges, { name: '', model_id: '', runtime_arn: '', endpoint: '', api_key_ssm: '', enabled: true }] })} style={{ fontSize: '11px', marginBottom: '10px' }}>+ Add judge</button>
      <details style={{ fontSize: '12px', marginBottom: '10px' }}>
        <summary style={{ cursor: 'pointer' }}>Load judges from Terraform (<code>terraform output -raw council_admin_json</code>)</summary>
        <textarea rows={3} value={paste} onChange={e => setPaste(e.target.value)} style={{ ...inp, width: '100%', marginTop: '6px', fontFamily: 'monospace' }} placeholder='[{"name":"claude","model_id":"…","runtime_arn":"arn:aws:bedrock-agentcore:…"}]' />
        <button onClick={applyPaste} style={{ fontSize: '11px', marginTop: '4px' }}>Load</button>
      </details>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {num('disagree_spread', 5, 100)}{num('min_win_rate', 0, 100)}{num('max_prompts', 1, 20)}{num('compare_prompts', 1, 12)}
        <label style={{ fontSize: '11px', alignSelf: 'flex-end' }}><input type="checkbox" checked={!!c.both_orders} onChange={e => setC({ ...c, both_orders: e.target.checked })} /> Judge Compare in both A/B orders</label>
        <label style={{ fontSize: '11px', alignSelf: 'flex-end' }}><input type="checkbox" checked={!!c.include_safety} onChange={e => setC({ ...c, include_safety: e.target.checked })} /> Include safety prompts</label>
      </div>
      <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '10px', marginBottom: '10px' }}>
        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input type="checkbox" checked={!!c.chief_judge}
            onChange={e => setC({ ...c, chief_judge: e.target.checked ? { model_id: '' } : null })} />
          Chief judge — one LLM reads all panel verdicts and writes the final score
        </label>
        {c.chief_judge && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', marginLeft: '22px', fontSize: '11px', flexWrap: 'wrap' }}>
            <span>Chief model</span>
            <input value={(c.chief_judge && c.chief_judge.model_id) || ''} placeholder="blank = default (EVAL_MODEL_ID); or a Bedrock model id"
              onChange={e => setC({ ...c, chief_judge: { ...(c.chief_judge || {}), model_id: e.target.value } })}
              style={{ ...inp, width: '340px' }} />
            <input value={(c.chief_judge && c.chief_judge.endpoint) || ''} placeholder="or self-hosted endpoint http://…:8000/v1"
              onChange={e => setC({ ...c, chief_judge: { ...(c.chief_judge || {}), endpoint: e.target.value } })}
              style={{ ...inp, width: '240px' }} />
            {(c.chief_judge && c.chief_judge.endpoint) ? (
              <input value={(c.chief_judge && c.chief_judge.api_key_ssm) || ''} placeholder="/eea/council/oss-judge-api-key"
                onChange={e => setC({ ...c, chief_judge: { ...(c.chief_judge || {}), api_key_ssm: e.target.value } })}
                style={{ ...inp, width: '220px' }} />
            ) : null}
            <span style={{ color: '#718096' }}>The panel's median is kept for reference; the chief's score becomes the final Golden Suite score.</span>
          </div>
        )}
      </div>
      <button onClick={() => { setMsg(''); saveAdminConfig('council', c); }}
        style={{ padding: '8px 16px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Save council settings</button>
      {msg && <span style={{ fontSize: '11px', color: '#555', marginLeft: '10px' }}>{msg}</span>}
    </div>
  );
}

// Re-run the shared open-weights classifier (backend model_availability.py) over rows
// ALREADY in the catalog, e.g. after a new family (Beam 1.0, Muse Spark) is added.
// Preview first (dry run, nothing written), then Apply. Upgrade-only: compliance,
// governance and VM fields are never touched. Gated on promote_market_to_other (same
// privilege the backend checks).
function RelabelOpenWeightsPanel({ onApplied }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);   // { changed, details } from a dry run
  const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

  const call = async (dryRun) => {
    setBusy(true); setMsg(dryRun ? 'Checking catalog…' : 'Relabelling…');
    try {
      const res = await fetch('/api/relabel-availability', { method: 'POST', headers: headers(), body: JSON.stringify({ dry_run: dryRun }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.status !== 'SUCCESS') { setMsg(d.error || `Request failed (${res.status})`); return; }
      if (dryRun) {
        setPreview(d);
        setMsg(d.changed ? `${d.changed} row(s) would change. Review, then Apply.` : 'Nothing to change: every row already matches the classifier.');
      } else {
        setPreview(null);
        setMsg(`Relabelled ${d.changed} row(s).${d.changed ? ' Catalog reloaded.' : ''}`);
        if (d.changed && onApplied) onApplied();
      }
    } catch (e) { setMsg('Error contacting server'); }
    finally { setBusy(false); }
  };

  const kindLabel = { now_self_hostable: 'Now self-hostable', size_fields_added: 'Size / repo added' };
  const rows = (preview && preview.details) || [];
  const btn = (bg) => ({ padding: '8px 16px', border: 'none', background: bg, color: '#fff', borderRadius: '4px',
    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, fontWeight: 'bold' });

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
      <h3 style={{ marginTop: 0 }}>🔓 Relabel Open Weights</h3>
      <p style={{ fontSize: '12px', color: '#666' }}>
        Re-checks models already in the catalog against the current open-weights family list (e.g. after Beam 1.0
        or Muse Spark is added) and updates their Self-Hosted label, size and Tier. Upgrade only: it never removes an
        open-weights label and never changes compliance, governance or VM assignments. Preview first; nothing is
        written until you Apply.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button disabled={busy} onClick={() => call(true)} style={btn('#0073bb')}>Preview changes</button>
        <button disabled={busy || !preview || !preview.changed} onClick={() => call(false)}
          title={!preview ? 'Run Preview first' : ''} style={btn(preview && preview.changed ? '#28a745' : '#a0aec0')}>
          Apply{preview && preview.changed ? ` (${preview.changed})` : ''}</button>
        {msg && <span style={{ fontSize: '12px', color: '#555' }}>{msg}</span>}
      </div>
      {rows.length > 0 && (
        <div style={{ marginTop: '12px', maxHeight: '260px', overflowY: 'auto', border: '1px solid #edf2f7', borderRadius: '6px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f7fafc', textAlign: 'left', position: 'sticky', top: 0 }}>
                <th style={{ padding: '6px 8px' }}>Model</th>
                <th style={{ padding: '6px 8px' }}>Change</th>
                <th style={{ padding: '6px 8px' }}>Architecture</th>
                <th style={{ padding: '6px 8px' }}>Tier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.model_id} style={{ borderTop: '1px solid #edf2f7' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.model_id}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {kindLabel[r.kind] || r.kind}
                    {r.kind === 'now_self_hostable' && r.exact === false &&
                      <span title="This hosted version is API-only; an official open-weight release of the family can be self-hosted"
                        style={{ marginLeft: '6px', fontSize: '10px', color: '#7a4b00', background: '#fff0d6', borderRadius: '8px', padding: '1px 6px' }}>family release</span>}
                  </td>
                  <td style={{ padding: '6px 8px', color: '#555' }}>
                    {r.from === r.to ? (r.to || '—') : <>{r.from || '—'} <span style={{ color: '#a0aec0' }}>→</span> <strong>{r.to}</strong></>}
                  </td>
                  <td style={{ padding: '6px 8px', color: '#555' }}>
                    {r.tier_from === r.tier_to ? (r.tier_to || '—') : <>{r.tier_from || '—'} <span style={{ color: '#a0aec0' }}>→</span> <strong>{r.tier_to}</strong></>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.changed > rows.length && <div style={{ padding: '6px 8px', fontSize: '11px', color: '#888' }}>Showing first {rows.length} of {preview.changed}.</div>}
        </div>
      )}
    </div>
  );
}

export default function AdminView() {
  return <AdminViewInner />;
}

// Audit Log viewer: reads a day's events from /api/audit (newest first) with
// filters. Self-contained (own state + fetch) like the other admin panels.
// Gated by the caller on view_audit || manage_users.
function AuditLogPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = React.useState(today);
  const [fUser, setFUser] = React.useState('');
  const [fFeature, setFFeature] = React.useState('');
  const [fStatus, setFStatus] = React.useState('');
  const [events, setEvents] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [expanded, setExpanded] = React.useState(null);
  const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
  const load = React.useCallback(() => {
    setBusy(true); setMsg('');
    const qs = new URLSearchParams({ date });
    if (fUser) qs.set('username', fUser);
    if (fFeature) qs.set('feature', fFeature);
    if (fStatus) qs.set('status', fStatus);
    fetch(`/api/audit?${qs.toString()}`, { headers: headers() })
      .then(r => r.json())
      .then(d => { setEvents(Array.isArray(d.events) ? d.events : []); setMsg(`${(d.events || []).length} event(s) for ${d.date || date}`); })
      .catch(() => setMsg('Failed to load audit log'))
      .finally(() => setBusy(false));
    /* eslint-disable-next-line */
  }, [date, fUser, fFeature, fStatus]);
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  const inp = { padding: '6px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px' };
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
      <h3 style={{ marginTop: 0 }}>🧾 Audit Log</h3>
      <p style={{ fontSize: '12px', color: '#666', marginTop: '-6px' }}>
        Daily record of user activity, errors, and persona chat Q&amp;A across the app. Timestamps are America/Denver (MST/MDT). Retained 90 days.
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        <label style={{ fontSize: '12px' }}>Date <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} /></label>
        <input placeholder="user" value={fUser} onChange={e => setFUser(e.target.value)} style={{ ...inp, width: '110px' }} />
        <select value={fFeature} onChange={e => setFFeature(e.target.value)} style={inp}>
          <option value="">all features</option>
          <option value="auth">auth</option>
          <option value="admin">admin</option>
          <option value="persona">persona</option>
          <option value="lab_vending">lab_vending</option>
          <option value="model_eval">model_eval</option>
        </select>
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={inp}>
          <option value="">all</option>
          <option value="ok">ok</option>
          <option value="error">error</option>
        </select>
        <button onClick={load} disabled={busy} style={{ padding: '6px 14px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
          {busy ? 'Loading…' : '↻ Refresh'}
        </button>
        {msg && <span style={{ fontSize: '11px', color: '#555' }}>{msg}</span>}
      </div>
      <div style={{ maxHeight: '460px', overflow: 'auto', border: '1px solid #edf2f7', borderRadius: '6px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#f7fafc', textAlign: 'left', position: 'sticky', top: 0 }}>
              <th style={{ padding: '6px' }}>Time (MT)</th>
              <th style={{ padding: '6px' }}>User</th>
              <th style={{ padding: '6px' }}>Feature</th>
              <th style={{ padding: '6px' }}>Action</th>
              <th style={{ padding: '6px' }}>Status</th>
              <th style={{ padding: '6px' }}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '12px', color: '#a0aec0', textAlign: 'center' }}>No events.</td></tr>
            )}
            {events.map((e, i) => {
              const isChat = e.feature === 'persona' && (e.prompt || e.response);
              return (
                <React.Fragment key={e.ts_id || i}>
                  <tr style={{ borderTop: '1px solid #edf2f7', cursor: isChat ? 'pointer' : 'default' }}
                      onClick={() => isChat && setExpanded(expanded === i ? null : i)}>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{e.ts_local || ''}</td>
                    <td style={{ padding: '6px' }}>{e.username || ''}</td>
                    <td style={{ padding: '6px' }}>{e.feature || ''}</td>
                    <td style={{ padding: '6px' }}>{e.action || ''}{isChat ? ' ▸' : ''}</td>
                    <td style={{ padding: '6px', color: e.status === 'error' ? '#b00020' : '#28a745', fontWeight: 'bold' }}>{e.status || ''}</td>
                    <td style={{ padding: '6px', color: '#555', wordBreak: 'break-word' }}>{e.detail || ''}</td>
                  </tr>
                  {isChat && expanded === i && (
                    <tr style={{ background: '#fbfcfe' }}>
                      <td colSpan={6} style={{ padding: '8px 12px' }}>
                        <div style={{ fontSize: '12px', marginBottom: '6px' }}><strong>Q:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{e.prompt || ''}</span></div>
                        <div style={{ fontSize: '12px' }}><strong>A:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{e.response || ''}</span></div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminViewInner() {
  const { adminConfig, adminUsers, allPrivileges, can, deleteUser, fetchCatalogs, handleTfTest, newUser, privileges, role, saveAdminConfig, saveUser, setNewUser, tfTestResult, token, username } = useApp();

    const cfg = adminConfig || {};
    const scoring = cfg.scoring || {};
    const tf = cfg.truefoundry || {};
    // Admin sub-tabs: 'config' (all settings panels) vs 'audit' (audit log).
    // A pure auditor (view_audit but no manage_config) lands on the audit tab.
    const [adminTab, setAdminTab] = React.useState(can('manage_config') ? 'config' : 'audit');
    return (
      <div style={{ marginTop: '10px' }}>
        <h2 style={{ color: '#0f2027' }}>⚙️ Admin Configuration</h2>

        {/* Admin sub-tabs: Configuration vs Audit Log */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0', marginBottom: '16px' }}>
          {can('manage_config') && (
            <button onClick={() => setAdminTab('config')} style={{ padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', borderBottom: adminTab === 'config' ? '3px solid #0073bb' : '3px solid transparent', color: adminTab === 'config' ? '#0073bb' : '#718096' }}>Configuration</button>
          )}
          {(can('view_audit') || can('manage_users')) && (
            <button onClick={() => setAdminTab('audit')} style={{ padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', borderBottom: adminTab === 'audit' ? '3px solid #0073bb' : '3px solid transparent', color: adminTab === 'audit' ? '#0073bb' : '#718096' }}>🧾 Audit Log</button>
          )}
        </div>

        {/* AUDIT LOG TAB */}
        {adminTab === 'audit' && (can('view_audit') || can('manage_users')) && <AuditLogPanel />}

        {/* CONFIGURATION TAB */}
        {adminTab === 'config' && (<>
        {/* GPU worker power control (start/stop to save cost) */}
        {can('manage_config') && <GpuWorkerPanel />}
      {can('manage_config') && <CouncilOssGpuPanel />}
        {/* Relabel catalog rows against the shared open-weights family list */}
        {can('promote_market_to_other') && <RelabelOpenWeightsPanel onApplied={() => fetchCatalogs && fetchCatalogs()} />}

        {/* Scoring config */}
        {can('manage_config') && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
            <h3 style={{ marginTop: 0 }}>Scoring Configuration</h3>
            <p style={{ fontSize: '12px', color: '#666' }}>Controls the Golden Suite base/tier boosts, the blended-score weights (weight_internal + weight_public, auto-normalized to 1.0), and the EchoStar-Approved threshold (final score 0-100).</p>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {['golden_base','tier_boost_high','tier_boost_low','weight_internal','weight_public','approved_threshold'].map(k => (
                <label key={k} style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {k}
                  <input type="number" step="0.1" defaultValue={scoring[k]}
                    onChange={e => { scoring[k] = parseFloat(e.target.value); }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px', width: '120px' }} />
                </label>
              ))}
            </div>
            <button onClick={() => saveAdminConfig('scoring', scoring)}
              style={{ marginTop: '12px', padding: '8px 16px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>
              Save Scoring Config</button>
          </div>
        )}

        {/* TrueFoundry config */}
        {can('manage_config') && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
            <h3 style={{ marginTop: 0 }}>TrueFoundry Configuration</h3>
            <p style={{ fontSize: '12px', color: '#666' }}>
              Control-plane URL manages VM membership; gateway is for inference. The token is write-only
              (never returned) — {tf.api_token_set ? 'a token is currently set.' : 'no token is set yet.'}
            </p>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '260px' }}>
                Control-plane API base
                <input defaultValue={tf.api_base} onChange={e => { tf.api_base = e.target.value; }}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
              </label>
              <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '260px' }}>
                Gateway base
                <input defaultValue={tf.gateway_base} onChange={e => { tf.gateway_base = e.target.value; }}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
              </label>
              <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '160px' }}>
                VM group
                <input defaultValue={tf.vm_group} onChange={e => { tf.vm_group = e.target.value; }}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
              </label>
              <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '260px' }}>
                API token (write-only)
                <input type="password" placeholder={tf.api_token_set ? '•••••••• (set)' : 'paste token to set'}
                  onChange={e => { tf.api_token = e.target.value; }}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
              </label>
            </div>
            <div style={{ marginTop: '14px', borderTop: '1px dashed #cbd5e0', paddingTop: '12px' }}>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0' }}>
                <strong>Advanced endpoint</strong> — the virtual-model REST path isn't publicly documented, so set it to match your tenant.
                Use "Test Connection" to probe before pushing. If a full URL is set it overrides base + path + resource.
              </p>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '150px' }}>
                  API path
                  <input defaultValue={tf.api_path || '/api/svc/v1'} onChange={e => { tf.api_path = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '150px' }}>
                  VM resource
                  <input defaultValue={tf.vm_resource || 'llm-gateway/model/enabled'} onChange={e => { tf.vm_resource = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '110px' }}>
                  Write method
                  <select defaultValue={tf.write_method || 'PUT'} onChange={e => { tf.write_method = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}>
                    <option>PUT</option><option>POST</option>
                  </select>
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '130px' }}>
                  Auth style
                  <select defaultValue={tf.auth_style || 'bearer'} onChange={e => { tf.auth_style = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}>
                    <option value="bearer">Bearer</option>
                    <option value="x-api-key">x-api-key</option>
                    <option value="raw">raw</option>
                  </select>
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '300px' }}>
                  Full VM URL override (optional)
                  <input defaultValue={tf.vm_full_url || ''} placeholder="https://.../virtual-models"
                    onChange={e => { tf.vm_full_url = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
              <button onClick={() => { const out = { ...tf }; if (!out.api_token) delete out.api_token; saveAdminConfig('truefoundry', out); }}
                style={{ padding: '8px 16px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>
                Save TrueFoundry Config</button>
              <button onClick={handleTfTest}
                style={{ padding: '8px 16px', border: '1px solid #0073bb', background: '#eef6fc', color: '#0073bb', borderRadius: '4px', cursor: 'pointer' }}>
                Test Connection</button>
            </div>
            {tfTestResult && (
              <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', fontSize: '12px',
                background: tfTestResult.__loading ? '#f7fafc' : (tfTestResult.ok ? '#f0fff4' : '#fff5f5'),
                border: `1px solid ${tfTestResult.ok ? '#28a745' : '#dc3545'}` }}>
                {tfTestResult.__loading ? 'Testing…' : (
                  <div>
                    <strong>{tfTestResult.ok ? '✅ Reachable' : '⚠️ Not reachable'}</strong> — status {String(tfTestResult.status)}<br/>
                    <span style={{ color: '#555' }}>URL: {tfTestResult.effective_url}</span><br/>
                    {tfTestResult.body_snippet && <span style={{ color: '#777' }}>Response: {tfTestResult.body_snippet}</span>}
                    {tfTestResult.message && <span style={{ color: '#777' }}>{tfTestResult.message}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* GitLab (Lab Vending provisioning) config */}
        {can('manage_config') && (() => {
          const gl = (adminConfig && adminConfig.gitlab) || {};
          return (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
              <h3 style={{ marginTop: 0 }}>Lab Vending — GitLab Provisioning</h3>
              <p style={{ fontSize: '12px', color: '#666' }}>
                Lets the Provision/Destroy buttons trigger the GitLab pipeline that runs Terraform on the self-hosted runner.
                The two tokens are write-only (never returned) — {gl.trigger_token_set ? 'a trigger token is set' : 'no trigger token set'};
                {' '}{gl.callback_token_set ? 'a callback token is set' : 'no callback token set'}.
              </p>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '160px' }}>
                  Project ID (numeric)
                  <input defaultValue={gl.project_id} onChange={e => { gl.project_id = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '120px' }}>
                  Ref (branch)
                  <input defaultValue={gl.ref || 'main'} onChange={e => { gl.ref = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '280px' }}>
                  Callback URL base
                  <input defaultValue={gl.callback_url} placeholder="https://<app-host>/api/lab-vends" onChange={e => { gl.callback_url = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '240px' }}>
                  Trigger token (write-only)
                  <input type="password" placeholder={gl.trigger_token_set ? '•••••••• (set)' : 'paste trigger token'}
                    onChange={e => { gl.trigger_token = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '240px' }}>
                  Callback token (write-only)
                  <input type="password" placeholder={gl.callback_token_set ? '•••••••• (set)' : 'generate + paste a shared secret'}
                    onChange={e => { gl.callback_token = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '240px' }}>
                  API token (read — polls pipeline status)
                  <input type="password" placeholder={gl.api_token_set ? '•••••••• (set)' : 'project/personal access token (read_api)'}
                    onChange={e => { gl.api_token = e.target.value; }}
                    style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                </label>
              </div>
              <button onClick={() => { const out = { ...gl }; if (!out.trigger_token) delete out.trigger_token; if (!out.callback_token) delete out.callback_token; if (!out.api_token) delete out.api_token; saveAdminConfig('gitlab', out); }}
                style={{ marginTop: '12px', padding: '8px 16px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>
                Save GitLab Config</button>
            </div>
          );
        })()}

        {/* LLM council */}
        {can('manage_config') && <CouncilPanel adminConfig={adminConfig} saveAdminConfig={saveAdminConfig} />}

        {/* Challenger ranking-agent cadence (mirrors the Challenger tab control) */}
        {can('manage_config') && <ChallengerSchedulePanel adminConfig={adminConfig} saveAdminConfig={saveAdminConfig} />}

        {/* Model sources */}
        {can('manage_config') && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
            <h3 style={{ marginTop: 0 }}>Model Sources (Aggregation)</h3>
            <p style={{ fontSize: '12px', color: '#666' }}>
              Which sources "Evaluate All" and seeding pull from. Artificial Analysis provides real public
              benchmark scores and needs an API key (write-only). Fetches run on the server.
            </p>
            {(() => {
              const src = (adminConfig && adminConfig.sources) || {};
              return (
                <div>
                  <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {['huggingface','openrouter','benchlm','bedrock','artificial_analysis'].map(s => (
                      <label key={s} style={{ fontSize: '12px' }}>
                        <input type="checkbox" defaultChecked={src[s] !== false}
                          onChange={e => { src[s] = e.target.checked; }} /> {s}
                      </label>
                    ))}
                    <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      HF limit
                      <input type="number" defaultValue={src.hf_limit || 650}
                        onChange={e => { src.hf_limit = parseInt(e.target.value, 10); }}
                        style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px', width: '100px' }} />
                    </label>
                  </div>
                  <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px', maxWidth: '420px' }}>
                    Artificial Analysis API key (write-only)
                    <input type="password" placeholder={src.artificial_analysis_api_key_set ? '•••••••• (set)' : 'paste key to set'}
                      onChange={e => { src.artificial_analysis_api_key = e.target.value; }}
                      style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                  </label>
                  <div>
                    <button onClick={() => { const out = { ...src }; if (!out.artificial_analysis_api_key) delete out.artificial_analysis_api_key; saveAdminConfig('sources', out); }}
                      style={{ marginTop: '12px', padding: '8px 16px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>
                      Save Sources Config</button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* User management */}
        {can('manage_users') && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px' }}>
            <h3 style={{ marginTop: 0 }}>User Access & Privileges</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f7fafc', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>User</th>
                  <th style={{ padding: '8px' }}>Role</th>
                  <th style={{ padding: '8px' }}>Privileges</th>
                  <th style={{ padding: '8px' }}>Active</th>
                  <th style={{ padding: '8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {adminUsers.map(u => (
                  <tr key={u.username} style={{ borderBottom: '1px solid #edf2f7' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{u.username}</td>
                    <td style={{ padding: '8px' }}>{u.role}</td>
                    <td style={{ padding: '8px', color: '#555' }}>{u.role === 'admin' ? 'ALL (admin)' : (u.privileges || []).join(', ') || '—'}</td>
                    <td style={{ padding: '8px' }}>{u.active === false ? 'no' : 'yes'}</td>
                    <td style={{ padding: '8px' }}>
                      <button onClick={() => setNewUser({ username: u.username, ['pass' + 'word']: '', role: u.role || 'viewer', privileges: (u.privileges || []), active: u.active !== false })}
                        style={{ padding: '3px 8px', fontSize: '10px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '3px', cursor: 'pointer', marginRight: '6px' }}>Edit</button>
                      <button onClick={() => deleteUser(u.username)}
                        style={{ padding: '3px 8px', fontSize: '10px', border: 'none', background: '#dc3545', color: '#fff', borderRadius: '3px', cursor: 'pointer' }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '16px', borderTop: '2px dashed #cbd5e0', paddingTop: '14px' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Add / Update User</h4>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                <input placeholder="username" value={newUser.username}
                  onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                <input type="password" placeholder="password (blank = keep/default)"
                  onChange={e => setNewUser({ ...newUser, ['pass' + 'word']: e.target.value })}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                  style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }}>
                  <option value="viewer">viewer</option>
                  <option value="user">user</option>
                  <option value="admin">admin (all privileges)</option>
                </select>
                <label style={{ fontSize: '12px' }}>
                  <input type="checkbox" checked={newUser.active}
                    onChange={e => setNewUser({ ...newUser, active: e.target.checked })} /> active
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {(allPrivileges || []).map(p => (
                  <label key={p} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input type="checkbox" disabled={newUser.role === 'admin'}
                      checked={newUser.role === 'admin' || newUser.privileges.includes(p)}
                      onChange={e => {
                        const set = new Set(newUser.privileges);
                        if (e.target.checked) set.add(p); else set.delete(p);
                        setNewUser({ ...newUser, privileges: Array.from(set) });
                      }} />
                    {p}
                  </label>
                ))}
              </div>
              <button onClick={() => saveUser(newUser)} disabled={!newUser.username}
                style={{ padding: '8px 16px', border: 'none', background: '#28a745', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>
                Save User</button>
            </div>
          </div>
        )}
        </>)}
      </div>
    );
  
}
